import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import { SurfacePalette, type as typo } from '../tokens'
import {
  PacketModalShell,
  modalGhostButtonStyle,
  modalInputStyle,
  modalPrimaryButtonStyle,
} from './PacketModalShell'

type Props = {
  palette: SurfacePalette
  creativeName: string
  onApprove: (input: { clientApproverName: string; notes?: string }) => Promise<void>
  onClose: () => void
}

/**
 * Captures the AE-attested client approval for a creative: the name of the
 * client who approved (required) plus optional notes. The AE is attesting on
 * the client's behalf, recorded as an external review by the write path.
 * Palette-aware so it reads correctly on both the light Edit surface and the
 * dark Present deck (where the AE records approval live with the client present).
 */
export function ClientApprovalModal({ palette, creativeName, onApprove, onClose }: Props) {
  const [clientApproverName, setClientApproverName] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const trimmedName = clientApproverName.trim()

  const handleApprove = async () => {
    if (!trimmedName || saving) return
    setSaving(true)
    try {
      await onApprove({ clientApproverName: trimmedName, notes: notes.trim() || undefined })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const labelStyle: React.CSSProperties = {
    fontSize: typo.size.body,
    fontWeight: typo.weight.semibold,
    color: palette.text,
  }

  return (
    <PacketModalShell
      palette={palette}
      title="Record client approval"
      subtitle={`Approving “${creativeName}” on behalf of the client.`}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={labelStyle}>Client approver name *</label>
        <input
          type="text"
          value={clientApproverName}
          onChange={e => setClientApproverName(e.target.value)}
          placeholder="Who approved this creative?"
          style={modalInputStyle(palette)}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={labelStyle}>Notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Optional context for this approval"
          style={{ ...modalInputStyle(palette), resize: 'none' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          style={modalGhostButtonStyle(palette)}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApprove}
          disabled={!trimmedName || saving}
          style={{
            ...modalPrimaryButtonStyle(palette),
            opacity: !trimmedName || saving ? 0.6 : 1,
            cursor: !trimmedName || saving ? 'default' : 'pointer',
          }}
        >
          {saving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Approve'}
        </button>
      </div>
    </PacketModalShell>
  )
}
